import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnDestroy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import orderBy from 'lodash/orderBy';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { catchError, filter, map, shareReplay, switchMap } from 'rxjs/operators';

import { ConfirmRestoreDialog, ConfirmRestoreDialogResult } from './confirm-restore-dialog.component';
import { DeleteDialogComponent, DeleteDialogResult } from '../../dialogs/delete-dialog/delete-dialog.component';
import { FileInfoDialog } from '../file-info-dialog/file-info-dialog.component';
import { CoreApiService } from '../../core-api.service';
import { LoggingService } from '../../logging.service';
import { NotificationService } from '../../notification.service';
import { ProjectsService } from '../../projects/projects.service';
// import { SessionService } from '../../sessions/session.service';
import { ContainerType } from '../../shared/enums/container-type.enum';
import { PermissionAction } from '../../shared/enums/permission-action.enum';
import { getOriginAction } from '../../shared/file.utils';
import { File, FileOrigin, FileVersion } from '../../shared/models/file.model';
import { OriginType } from '../../shared/models/origin.model';
import { TranslationBundle, TranslationService } from '../../translation.service';
import { DialogService } from '../../dialogs';

interface FileVersionDialogData {
  file: Pick<File, 'file_id'>
}

@Component({
  selector: 'file-version-dialog',
  host: {
    class: 'mat-dialog-component',
  },
  styleUrls: ['./file-version-dialog.component.scss'],
  templateUrl: './file-version-dialog.component.html',
})
export class FileVersionDialog implements OnDestroy {
  error: Error = null
  file = this.data.file as File
  modified = false // dialog close value
  permissions = {
    createUpload: false,
    deleteDevice: false,
    deleteNonDevice: false,
  }
  versions$: Observable<FileVersion[]> = null

  private messages = {
    dialog: TranslationBundle.EMPTY,
    fileOriginActions: TranslationBundle.EMPTY,
  }
  private subscriptions = {
    file: Subscription.EMPTY,
    messages: Subscription.EMPTY,
    permissions: Subscription.EMPTY,
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: FileVersionDialogData,
    private dialog: DialogService,
    private coreApi: CoreApiService,
    private logger: LoggingService,
    private notifications: NotificationService,
    private projectsService: ProjectsService,
    private ref: MatDialogRef<FileVersionDialog, boolean>,
    // TODO: Add back session service
    // private sessionService: SessionService,
    private translate: TranslationService,
  ) {
    this.dialog.registerDialog(FileVersionDialog, { multi: 'replace' });
    this.ref.backdropClick().subscribe(() => {
      this.ref.close(this.modified);
    });

    const file$ = ['name', 'parents', 'version'].every(key => key in this.file)
      ? of(this.file)
      : this.coreApi.files.get(this.file.file_id).pipe(
        catchError(error => {
          this.logger.error(error);
          return of(this.file);
        }),
        shareReplay(1),
      );
    this.subscriptions.file = file$.subscribe(file => {
      this.file = file;
    });

    this.subscriptions.messages = combineLatest([
      this.translate.getBundle('fileVersionDialog'),
      this.translate.getBundle('fileOriginActions'),
    ]).subscribe(([dialog, fileOriginActions]) => {
      this.messages = { dialog, fileOriginActions };
    });

    this.subscriptions.permissions = file$.pipe(
      switchMap(file => file.parents?.project ? this.projectsService.getProjectPermissions(file.parents.project) : of([]))
    ).subscribe(permissions => {
      this.permissions = {
        createUpload: permissions.includes(PermissionAction.FilesCreateUpload) || permissions.includes(PermissionAction.FilesCreateSingle),
        deleteDevice: permissions.includes(PermissionAction.FilesDeleteDeviceData),
        deleteNonDevice: permissions.includes(PermissionAction.FilesDeleteNonDeviceData),
      };
    });
    this.loadVersions();
  }

  ngOnDestroy(): void {
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
  }

  private loadVersions(): void {
    this.error = null;
    this.versions$ = this.coreApi.files.versions(this.data.file.file_id).pipe(
      map(versions => orderBy(versions, 'version', 'desc')),
      catchError((error: HttpErrorResponse) => {
        this.error = error;
        if (error.status !== 404) {
          this.logger.error(error);
        }
        return of([]);
      }),
      shareReplay(1),
    );
  }

  private notifyContainerChange(): void {
    this.modified = true;
    if (this.file.parent_ref.type === ContainerType.Acquisition) {
      // this.sessionService.acquisitionChange$.next(null);
    }
  }

  getOriginAction(version: FileVersion): string {
    const action = getOriginAction(version, this.file);
    return this.messages.fileOriginActions.get(action);
  }

  getOrigin(version: FileVersion): FileOrigin {
    return version.restored_by ?? version.origin;
  }

  get copySourceLabels(): { project: string, group: string } | null {
    if (!this.file?.copy_of) {
      return null;
    }
    const labels = {
      project: this.file.copy_of.project_label || this.file.copy_of.parents?.project,
      group: this.file.copy_of.group_label || this.file.copy_of.parents?.group,
    };
    return labels.project && labels.group ? labels : null;
  }

  getTranslation(key: string): string {
    return this.messages.dialog.get(key);
  }

  information(version: FileVersion): void {
    this.coreApi.files.get(version.file_id, { version: version.version }).pipe(
      switchMap(file => this.coreApi.containers.get(file.parent_ref.id).pipe(
        switchMap(container => this.dialog.open(FileInfoDialog, {
          data: { container, file, writable: false },
        }).afterClosed())
      )),
    ).subscribe();
  }

  restore(version: FileVersion): void {
    this.dialog.open(ConfirmRestoreDialog).afterClosed().pipe(
      filter(({ confirmRestore }) => confirmRestore),
      switchMap(({ evaluateGearRules }: ConfirmRestoreDialogResult) => this.coreApi.files.restore(version.file_id, { version: version.version, evaluate_gear_rules: evaluateGearRules })),
    ).subscribe({
      next: () => {
        this.loadVersions();
        this.notifyContainerChange();
      },
      error: error => {
        this.logger.error(error);
        this.notifications.error('files.restoreVersion.error');
      },
    });
  }

  canDelete(version: FileVersion): boolean {
    return version.origin.type === OriginType.Device ? this.permissions.deleteDevice : this.permissions.deleteNonDevice;
  }

  delete(version: FileVersion): void {
    this.dialog.open(DeleteDialogComponent, {
      data: { content: this.messages.dialog.get('dialogs.deleteVersionConfirm') },
    }).afterClosed().pipe(
      filter((result: DeleteDialogResult) => result.delete),
      switchMap(result => {
        const deleteParams: { version: number, delete_reason?: string } = { version: version.version };
        if (result.deleteReason) {
          deleteParams.delete_reason = result.deleteReason;
        }
        return this.coreApi.files.delete(version.file_id, deleteParams);
      }),
    ).subscribe({
      next: () => {
        this.loadVersions();
        this.notifyContainerChange();
      },
      error: error => {
        if (error.error?.reason === 'analysis_conflict') {
          this.notifications.error('files.deleteVersion.analysisConflict');
          return;
        }
        this.logger.error(error);
        this.notifications.error('files.deleteVersion.error');
      },
    });
  }
}
